import { useEffect, useState } from "react";
import "./Portfolio.css";
import KatnavImg from "../../assets/images/katnavWeb.png";
import KatnavWeb from "../../assets/images/katnavWebPrice.png";
import KatnavMob from "../../assets/images/katnavMob.png";
import KatnavSales from "../../assets/images/katnavMobSales.png";
import NavImg from "../../assets/images/shopAzNAV.png";
import TrimedNav from "../../assets/images/trimedNAv.jpg";
import SyncWebImg from "../../assets/images/syncWeb.png";
import SyncWebJobs from "../../assets/images/syncWebJobs.png";
import SyncWebProd from "../../assets/images/syncWebProd.png";
import PowerBi from "../../assets/images/powerBi_ShopAz.jpg";
import PowerBiImg from "../../assets/images/powerBi.jpg";
import HrWebImg from "../../assets/images/hrWeb.jpg";
import HrWebHoliday from "../../assets/images/hrWebHoliday.jpg";
import HrWebCalendar from "../../assets/images/hrWebCalendar.jpg";
import GpsImg from "../../assets/images/gps.png";
import GpsTrack from "../../assets/images/gpsImage.jpg";
import RelodeImg from "../../assets/images/relodeDesktop.jpg";
import RelodeImgBullet from "../../assets/images/relodDesportBullet.jpg";
import HillGroup from "../../assets/images/hillgroupWeb.jpg";
import HillGroupLog from "../../assets/images/hillGropuLogin.jpg";
import { Code, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

const ImageCarousel = ({ images, title, category }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [images.length, isAutoPlaying]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
    setIsAutoPlaying(false);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
    setIsAutoPlaying(false);
  };

  const goToImage = (index) => {
    setCurrentImage(index);
    setIsAutoPlaying(false);
  };

  if (images.length === 1) {
    return (
      <div className="portfolio-project-image-container">
        <img
          src={images[0] || "/placeholder.svg"}
          alt={title}
          className="portfolio-project-image"
        />
        <div className="portfolio-project-overlay">
          <div className="portfolio-project-category">{category}</div>
        </div>
        <div className="portfolio-image-animation-layer"></div>
      </div>
    );
  }

  return (
    <div className="portfolio-project-image-container portfolio-carousel-container">
      <div className="portfolio-carousel-wrapper">
        {images.map((image, index) => (
          <img
            key={index}
            src={image || "/placeholder.svg"}
            alt={`${title} - Image ${index + 1}`}
            className={`portfolio-project-image portfolio-carousel-image ${
              index === currentImage ? "active" : ""
            }`}
            style={{
              transform: `translateX(${(index - currentImage) * 100}%)`,
            }}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            className="portfolio-carousel-nav portfolio-carousel-prev"
            onClick={prevImage}
            aria-label="Previous image"
          >
            <ChevronLeft className="portfolio-nav-icon" />
          </button>
          <button
            className="portfolio-carousel-nav portfolio-carousel-next"
            onClick={nextImage}
            aria-label="Next image"
          >
            <ChevronRight className="portfolio-nav-icon" />
          </button>
        </>
      )}

      {/* Image Indicators */}
      {images.length > 1 && (
        <div className="portfolio-carousel-indicators">
          {images.map((_, index) => (
            <button
              key={index}
              className={`portfolio-indicator ${
                index === currentImage ? "active" : ""
              }`}
              onClick={() => goToImage(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="portfolio-image-counter">
          {currentImage + 1} / {images.length}
        </div>
      )}

      <div className="portfolio-project-overlay">
        <div className="portfolio-project-category">{category}</div>
      </div>
      <div className="portfolio-image-animation-layer"></div>
    </div>
  );
};

const Portfolio = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    projects: 0,
    clients: 0,
    years: 0,
  });

  // Scroll to top and trigger animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger entrance animations
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    // Animate counters
    const animateCounters = () => {
      const targets = { projects: 150, clients: 85, years: 10 };
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;

        setCounters({
          projects: Math.floor(targets.projects * progress),
          clients: Math.floor(targets.clients * progress),
          years: Math.floor(targets.years * progress),
        });

        if (step >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepTime);
    };

    const counterTimer = setTimeout(animateCounters, 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(counterTimer);
    };
  }, []);

  const projects = [
    {
      title: "KatNav Platform",
      description:
        "All-in-one platform for managing sales, purchases, stock registration, and procurement processes. KatNav integrates seamlessly with Microsoft Dynamics Business Central (NAV), a custom web portal, and mobile applications—ensuring users can manage operations from anywhere. The system centralizes inventory, tracks transactions in real-time, and simplifies workflows across departments, improving accuracy and operational efficiency.",
      images: [KatnavImg, KatnavSales, KatnavWeb, KatnavMob],
      category: "Business Management",
      technologies: [
        "Microsoft Dynamics NAV",
        "Web Portal",
        "Mobile Integration",
      ],
      results:
        "Improved inventory accuracy and streamlined sales/purchasing operations",
      delay: "0.3s",
    },
    {
      title:
        "Business Solutions Platform in Business Central",
      description:
        "A scalable, end-to-end business management solution built within Microsoft Dynamics 365 Business Central. Designed to unify key operations such as finance, sales, procurement, inventory, and service management. Enables seamless data flow, improves decision-making through real-time insights, and automates repetitive tasks to enhance efficiency across departments.",
      images: [NavImg, TrimedNav],
      category: "Enterprise Resource Planning (ERP)",
      technologies: [
        "Microsoft Dynamics 365 Business Central",
        "Power Platform Integration",
        "Cloud ERP",
        "AI-Driven Insights",
      ],
      results:
        "Improved cross-department collaboration, reduced operational costs, and enhanced scalability",
      delay: "0.2s",
    },
    {
      title: "VtexSync E-commerce Integration",
      description:
        "Unified e-commerce integration platform enabling seamless connection between multiple online stores and a centralized system. Automates synchronization of products, prices, stock levels, images, and descriptions. Streamlines operations through smart automated jobs, reducing manual effort and ensuring consistent data across channels.",
      images: [SyncWebImg, SyncWebJobs, SyncWebProd],
      category: "E-commerce Integration",
      technologies: ["VTEX", "API Integration", "Automation Workflows"],
      results:
        "Significant reduction in manual updates and inventory mismatches",
      delay: "0.3s",
    },
    {
      title: "Power BI Dynamic Reporting Platform",
      description:
        "Custom-built Power BI solution that delivers dynamic, real-time reporting tailored for any type of business or dataset. Connects seamlessly with various data sources to visualize KPIs, trends, and performance metrics through interactive dashboards. Enables decision-makers to filter, drill down, and analyze data across departments such as sales, finance, HR, operations, and marketing—all in one unified view.",
      images: [PowerBi, PowerBiImg],
      category: "Data Analytics & Reporting",
      technologies: [
        "Power BI",
        "Data Modeling",
        "API/Data Source Integration",
      ],
      results:
        "Enhanced data visibility and faster decision-making across all business units",
      delay: "0.3s",
    },
    {
      title: "SmartHR Workforce Management",
      description:
        "Comprehensive HR management system designed to streamline employee administration, leave tracking, and organizational workflows. Enables HR teams to efficiently manage worker profiles, holidays, attendance, and internal communications—all in one centralized platform. Improves transparency, reduces paperwork, and boosts overall HR productivity.",
      images: [HrWebImg, HrWebHoliday, HrWebCalendar],
      category: "Human Resources",
      technologies: ["Web Portal", "Role-Based Access", "Automation Workflows"],
      results: "30% time savings on HR administrative tasks",
      delay: "0.3s",
    },
    {
      title: "GPS Tracking System",
      description:
        "Real-time GPS tracking solution designed to monitor vehicles, assets, and personnel with precision and reliability. TrackWise provides live location updates, route history, speed monitoring, and geofencing alerts through an intuitive dashboard. Ideal for logistics companies, field service teams, and fleet managers looking to increase visibility, safety, and operational control across mobile operations.",
      images: [GpsImg, GpsTrack],
      category: "GPS & Fleet Management",
      technologies: [
        "GPS Integration",
        "Web Dashboard",
        "Mobile Support",
        "Real-Time Tracking",
      ],
      results: "Increased route efficiency and improved asset monitoring",
      delay: "0.3s",
    },
    {
      title: "Clothing & Sports Store Management System",
      description:
        "Complete retail management solution tailored for clothing and sports equipment stores. FitRetail streamlines inventory tracking, sales processing, product categorization (by size, color, brand), and customer management. The system supports barcode scanning, real-time stock updates, and promotions management—empowering store owners to maintain accurate inventory, speed up checkout, and enhance the shopping experience.",
      images: [HillGroupLog, HillGroup],
      category: "Retail Management",
      technologies: [
        "POS Integration",
        "Inventory Control",
        "Barcode Scanning",
        "Web & Desktop UI",
      ],
      results:
        "Faster checkout and optimized inventory for fashion and sports retailers",
      delay: "0.3s",
    },
    {
      title: "Shooting Range Desktop Application",
      description:
        "High-performance desktop application developed in .NET (C#) for managing shooting range activities and firearm sessions. TargetSharp enables users to track shooter performance, manage weapons inventory, schedule sessions, and record target accuracy in real time. Designed with a user-friendly interface and built for speed and reliability, the platform is ideal for shooting clubs, training facilities, and law enforcement programs.",
      images: [RelodeImg, RelodeImgBullet],
      category: "Shooting Range Management",
      technologies: [".NET Framework", "C#", "Windows Desktop UI"],
      results: "Enhanced session tracking and streamlined range operations",
      delay: "0.3s",
    },
  ];

  return (
    <div className="portfolio-main-container">
      {/* Hero Section */}
      <section className="portfolio-hero-wrapper">
        <div className="portfolio-hero-background">
          <div className="portfolio-hero-grid"></div>
          <div className="portfolio-floating-elements">
            <div className="portfolio-floating-element portfolio-element-1"></div>
            <div className="portfolio-floating-element portfolio-element-2"></div>
            <div className="portfolio-floating-element portfolio-element-3"></div>
          </div>
        </div>

        <div className="portfolio-content-width">
          <div
            className={`portfolio-hero-content ${
              isVisible ? "portfolio-fade-in" : ""
            }`}
          >
            <div className="portfolio-hero-badge">
              <Code className="portfolio-badge-icon" />
              <span>Our Solutions Portfolio</span>
            </div>
            <h1 className="portfolio-hero-title">
              Transforming Businesses with
              <span className="portfolio-gradient-text">
                {" "}
                Innovative ERP Solutions
              </span>
            </h1>
            <p className="portfolio-hero-description">
              Discover how we've helped companies across Albania optimize their
              operations with cutting-edge Microsoft Dynamics NAV solutions,
              mobile integrations, and comprehensive business management
              systems.
            </p>
            <div className="portfolio-hero-stats">
              <div className="portfolio-stat-item">
                <div className="portfolio-stat-number">
                  {counters.projects}+
                </div>
                <div className="portfolio-stat-label">Projects Completed</div>
              </div>
              <div className="portfolio-stat-item">
                <div className="portfolio-stat-number">{counters.clients}+</div>
                <div className="portfolio-stat-label">Happy Clients</div>
              </div>
              <div className="portfolio-stat-item">
                <div className="portfolio-stat-number">{counters.years}+</div>
                <div className="portfolio-stat-label">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="portfolio-projects-section">
        <div className="portfolio-content-width">
          <div className="portfolio-section-header">
            <h2 className="portfolio-section-title">Our Featured Projects</h2>
            <p className="portfolio-section-description">
              Discover how we've transformed businesses across Albania with
              innovative ERP solutions and cutting-edge implementations.
            </p>
          </div>

          <div className="portfolio-projects-grid">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`portfolio-project-card ${
                  isVisible ? "portfolio-slide-up" : ""
                }`}
                style={{ animationDelay: project.delay }}
              >
                <ImageCarousel
                  images={project.images}
                  title={project.title}
                  category={project.category}
                />

                <div className="portfolio-project-content">
                  <h3 className="portfolio-project-title">{project.title}</h3>
                  <p className="portfolio-project-description">
                    {project.description}
                  </p>

                  <div className="portfolio-project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="portfolio-tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="portfolio-project-results">
                    <div className="portfolio-results-badge">
                      <CheckCircle className="portfolio-results-icon" />
                      <span>{project.results}</span>
                    </div>
                  </div>

                  {/* <button className="portfolio-project-button">
                    <span>View Case Study</span>
                    <ArrowRight className="portfolio-button-icon" />
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
